import { t } from '@glory/i18n';
import { Bell } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '../components/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover.tsx';

/** Колокольчик со счётчиком непрочитанного и выпадающим центром уведомлений. */
export function NotificationBell({
  unread,
  children,
  onMarkAllRead,
  open,
  onOpenChange,
}: {
  unread: number;
  children: ReactNode;
  onMarkAllRead?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Popover {...(open === undefined ? {} : { open })} {...(onOpenChange ? { onOpenChange } : {})}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="relative w-8 px-0" aria-label={t('shell.notifications')}>
          {/* Счётчик привязан к значку, а не к кнопке: сидит в его углу и не закрывает колокольчик;
              обводка цветом фона отделяет его от значка. */}
          <span className="relative flex">
            <Bell />
            {unread > 0 ? (
              // Новое число мягко появляется: ключ меняется вместе со счётчиком.
              <span
                key={unread > 99 ? 'many' : unread}
                className="absolute -top-1.5 left-[calc(100%-0.4375rem)] grid h-3.5 min-w-3.5 animate-pop-in place-items-center rounded-full bg-accent px-[0.1875rem] text-[0.5625rem] leading-none font-extrabold text-on-accent tabular-nums ring-2 ring-bg"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,380px)] p-0">
        <div className="flex items-center justify-between border-b border-border-soft px-3 py-2">
          <span className="font-display text-body font-bold">{t('shell.notifications')}</span>
          {unread > 0 && onMarkAllRead ? (
            <Button size="sm" variant="ghost" onClick={onMarkAllRead}>
              {t('shell.markAllRead')}
            </Button>
          ) : null}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">{children}</div>
      </PopoverContent>
    </Popover>
  );
}

/** Строка уведомления в центре уведомлений; `count` больше одного — это группа одинаковых, «×5». */
export function NotificationItem({
  title,
  body,
  time,
  unread,
  count = 1,
  onClick,
}: {
  title: ReactNode;
  body?: ReactNode;
  time: ReactNode;
  unread: boolean;
  count?: number;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full gap-2.5 border-b border-border-soft px-3 py-2.5 text-left last:border-b-0 hover:bg-surface-2"
    >
      <span className={unread ? 'mt-1.5 size-2 shrink-0 rounded-full bg-accent' : 'mt-1.5 size-2 shrink-0'} />
      <span className="min-w-0 flex-1">
        <span className="block font-bold">
          {title}
          {count > 1 ? (
            <span className="ml-1.5 rounded-xs bg-surface-2 px-1 py-px align-middle text-caption font-bold text-muted tabular-nums">
              {t('shell.notificationsTimes', { count })}
            </span>
          ) : null}
        </span>
        {body ? <span className="block text-small text-muted">{body}</span> : null}
      </span>
      <span className="shrink-0 text-caption text-muted">{time}</span>
    </button>
  );
}
